import React, { useEffect, useState } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { Spin, Button, Breadcrumb, Row, Space, Typography, Input, Result, Form, notification } from "antd";
import { Loading3QuartersOutlined } from "@ant-design/icons";

import PageHeader from "../../../components/PageHeader";
import Overview from "../../../components/Shop/Overview";
import ShopEdit from "../../../components/ShopRequest/Edit";
import ShopStatus from "../../../components/Table/ShopStatus";
import useShopQuery from "../../../hooks/useQuery/useShop";

import _ from "lodash";
import log from "../../../utils/log";
import useShop from "../../../hooks/useShop";
import { showErrMsg } from "../../../utils/error";
import c from "../../../utils/common";

const { Title, Text } = Typography;

const SingleShop = () => {
  const history = useHistory();
  const location = useLocation(); // Monitor route changes
  const { id } = useParams(); // Get the :id parameter from the URL

  const { updateShop } = useShop();

  const [shop, setShop] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);

  const [shopForm] = Form.useForm();

  const {
    data: originalShop,
    isFetching: isFetchingOriginalShop,
    refetch,
    error: isShowErrorPage,
  } = useShopQuery(
    "shop/query",
    {
      id,
      populate: [
        { path: "company", select: "name br address" },
        { path: "brand", select: "name logo category" },
      ],
    },
    {
      staleTime: Infinity, // Prevent auto refetch
      onSuccess: (data) => {
        setShop(data);
      },
    }
  );

  useEffect(() => {
    // Trigger refetch when the page is entered
    refetch();
  }, [location, refetch]); // Run effect whenever location changes

  const handleReset = () => {
    setShop(originalShop);
  };

  const updateShopData = (newData) => {
    setShop((prev) => {
      const updated = _.merge({}, prev, newData.shop);
      log.debug(newData);

      // Explicitly overwrite certain fields instead of merging
      if ("gallery" in newData) {
        updated.gallery = newData.gallery;
      }

      log.debug(updated);
      return updated;
    });
  };

  const submitRequest = async () => {
    try {
      await shopForm.validateFields();
    } catch (error) {
      log.error(error);
      return notification.warning({
        description: (
          <Text style={{ fontSize: "1rem" }}>Some of the input fields are invalid. Please review and save again.</Text>
        ),
      });
    }

    const diff = c.getObjectDiff(originalShop, shop);

    if (!diff) {
      return notification.info({ description: "No information changed." });
    }

    try {
      setIsLoadingSubmit(true);
      await updateShop(id, diff);
      notification.success({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Success
          </Title>
        ),
      });
      setIsEdit(false);
      refetch();
    } catch (error) {
      showErrMsg(error);
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  return (
    <div style={{ margin: "0 auto" }}>
      <Spin
        spinning={isFetchingOriginalShop}
        indicator={<Loading3QuartersOutlined spin style={{ color: "white", fontSize: "2rem" }} />}
        fullscreen
      />

      {isShowErrorPage ? (
        <Result
          status="404"
          title="404"
          subTitle="Sorry, the page you visited does not exist."
          extra={
            <Button type="primary" onClick={() => window.location.reload()}>
              Refresh
            </Button>
          }
        />
      ) : (
        <>
          <Breadcrumb
            items={[{ title: <a onClick={() => history.push("/merchantUser/shop")}>Shop List</a> }, { title: "Shop" }]}
          />

          <PageHeader
            title={
              <Space>
                Shop Detail
                {shop ? <ShopStatus shop={shop} /> : null}
              </Space>
            }
            isSticky
            actionGroup={
              isEdit ? (
                <Space>
                  <Button
                    loading={isLoadingSubmit}
                    size="large"
                    type="text"
                    style={{ width: "7.5rem", color: "var(--ion-color-red-700)" }}
                    onClick={() => {
                      setIsEdit(false);
                      handleReset();
                    }}
                  >
                    cancel
                  </Button>
                  <Button
                    disabled={!originalShop || !c.hasDiff(originalShop, shop)}
                    loading={isLoadingSubmit}
                    size="large"
                    type="default"
                    style={{ width: "7.5rem" }}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                  <Button
                    disabled={!originalShop || !c.hasDiff(originalShop, shop)}
                    loading={isLoadingSubmit}
                    size="large"
                    type="primary"
                    style={{ width: "7.5rem" }}
                    onClick={() => submitRequest()}
                  >
                    Confirm
                  </Button>
                </Space>
              ) : (
                <Space>
                  <Button
                    disabled={isLoadingSubmit}
                    size="large"
                    type="primary"
                    style={{ width: "7.5rem" }}
                    onClick={() => setIsEdit(true)}
                  >
                    Edit
                  </Button>
                </Space>
              )
            }
            onBack={() => history.goBack()}
          />

          {!isFetchingOriginalShop && (
            <Row style={{ paddingTop: "2rem" }}>
              {isEdit ? (
                <ShopEdit
                  state={{
                    data: shop,
                    onChange: updateShopData,
                    form: shopForm,
                  }}
                  brand={shop?.brand}
                />
              ) : (
                <Overview
                  state={{
                    data: shop,
                    onChange: () => {},
                  }}
                />
              )}
            </Row>
          )}
        </>
      )}
    </div>
  );
};

export default SingleShop;
